import { Box, PopperPlacementType } from '@mui/material';
import React, { useEffect } from 'react';

import { delayedMobileCloseClickHandler } from '../Components/utils';
import { useMenuContext } from '../ContextState/useMenuContext';
import { OfflineIndicator } from '../UtilityTray/Components/OfflineIndicator';
import { itemHasNestedSubmenus, itemIsLeafNode, useEnhancedEffect } from '../utils/utils';

import { DATA_TEST } from '../enums';
import { IMenuOption } from '../types';

import { NAV_MENU_ITEM_FLEX_GAP } from '../Components/styleConstants';
import { LevelOneMenuItem } from './LevelOneMenuItem';
import { MoreMenuTab } from './MoreMenuTab';
import { SubmenuPopperContainer } from './SubmenuPopperContainer';
import { useNavResizeController } from './useNavResizeController';

interface ILevelOneMenuProps {
  navContainerRef: React.RefObject<HTMLDivElement> | null;
  customHeaderErrorMessage?: string;
  showOfflineIndicator?: boolean;
}

interface ISubmenuConfig {
  anchorEl: HTMLDivElement | null;
  placement: PopperPlacementType;
}

export const LevelOneMenu: React.FC<ILevelOneMenuProps> = ({
  navContainerRef,
  customHeaderErrorMessage,
  showOfflineIndicator
}) => {
  const {
    menuItems,
    showFavoritesSubmenu,
    disableFavorites,
    setShowFavoritesSubmenu,
    levelTwoLoadingError,
    currentLevelOneItem,
    submenuLoadingItemId,
    setCurrentLevelOneItem,
    currentLevelOneItemRef,
    currentFirstLevelTwoItemRef,
    favoritesRefs,
    showMoreMenu
  } = useMenuContext();

  const defaultState: ISubmenuConfig = { anchorEl: null, placement: 'auto' };

  const menuItemsContainerRef = React.useRef<HTMLDivElement>(null);
  const favoritesMenuItemRef = React.useRef<HTMLLIElement>(null);
  const moreMenuTabRef = React.useRef<HTMLLIElement>(null);
  const offlineIndicatorRef = React.useRef<HTMLDivElement>(null);

  const [dynamicMenuItemCount, setDynamicMenuItemCount] = React.useState(menuItems?.length);

  // Account for menu items updating over time
  // Failing to do this results in all newly added menu items being hidden immediately in the "More" menu
  useEffect(() => {
    if (menuItems?.length > 0) {
      setDynamicMenuItemCount(menuItems.length);
    }
  }, [menuItems?.length]);

  const menuItemAnchorRefs = menuItems?.slice(0, dynamicMenuItemCount).reduce((acc, item) => {
    acc[item.id] = React.createRef<HTMLLIElement>();
    return acc;
  }, {});

  const dynamicMenuItems = React.useMemo(() => {
    return menuItems?.slice(0, dynamicMenuItemCount) || [];
  }, [menuItems, dynamicMenuItemCount]);

  const overflowMenuItems = React.useMemo(() => {
    return menuItems?.slice(dynamicMenuItemCount) || [];
  }, [menuItems, dynamicMenuItemCount]);

  const showMoreMenuTab = overflowMenuItems?.length > 0;

  useNavResizeController({
    menuItemsContainerRef,
    isMoreMenuVisible: showMoreMenuTab,
    dynamicMenuItemCount: !disableFavorites ? dynamicMenuItemCount + 1 : dynamicMenuItemCount,
    totalMenuItemCount: !disableFavorites ? menuItems?.length + 1 : menuItems?.length,
    updateDynamicMenuItemCount: (delta: number) => {
      setDynamicMenuItemCount(dynamicMenuItemCount + delta);
    }
  });

  const [popperSubmenuConfig, setPopperSubmenuConfig] = React.useState<ISubmenuConfig>(defaultState);
  const setPopperSubmenuConfigForElement = (element: HTMLDivElement | null, levelThreeExists: boolean) => {
    if (element) {
      if (levelThreeExists) {
        setPopperSubmenuConfig({
          anchorEl: navContainerRef!.current,
          placement: 'bottom'
        });
      } else {
        setPopperSubmenuConfig({ anchorEl: element, placement: 'bottom-start' });
      }
    }
  };

  // An effect to handle how sub menus are rendered.
  // This logic is based on the presence of submenu sections and the current level one menu item
  useEnhancedEffect(() => {
    if (currentLevelOneItem?.id) {
      const activeElement = document.getElementById(currentLevelOneItem?.id);

      const menuItemHasLevelThreeSubmenuSections = itemHasNestedSubmenus(currentLevelOneItem);

      if (activeElement) {
        setPopperSubmenuConfigForElement(activeElement as HTMLDivElement, menuItemHasLevelThreeSubmenuSections);
      } else {
        const moreMenu = document.getElementById('more-menu-button');
        setPopperSubmenuConfigForElement(moreMenu as HTMLDivElement, menuItemHasLevelThreeSubmenuSections);
      }
    }
  }, [currentLevelOneItem, dynamicMenuItemCount, submenuLoadingItemId]);

  useEffect(() => {
    if (showFavoritesSubmenu && favoritesMenuItemRef) {
      currentLevelOneItemRef.current = favoritesMenuItemRef?.current;
    } else if (currentLevelOneItem?.id && menuItemAnchorRefs?.[currentLevelOneItem?.id]) {
      currentLevelOneItemRef.current = menuItemAnchorRefs?.[currentLevelOneItem?.id]?.current;
    } else if (showMoreMenu && moreMenuTabRef) {
      currentLevelOneItemRef.current = moreMenuTabRef?.current;
    }
  }, [currentLevelOneItem?.id, showFavoritesSubmenu, showMoreMenu]);

  return (
    <>
      <Box
        data-test={DATA_TEST.nav_menu_container}
        display="flex"
        ref={menuItemsContainerRef}
        flex={1}
        height="100%"
        role="menu"
        sx={{ gap: `${NAV_MENU_ITEM_FLEX_GAP}px`, ml: 1 }}
      >
        {!disableFavorites && (
          <LevelOneMenuItem
            ref={favoritesMenuItemRef}
            item={{
              id: 'favorites',
              label: 'Favorites',
              tooltipText: 'View favorite items'
            }}
            showFavoriteIcon={true}
            isSelected={showFavoritesSubmenu}
            firstLevelTwoItemRef={favoritesRefs?.[0]?.[0]}
            handleClick={() => {
              setShowFavoritesSubmenu(!showFavoritesSubmenu);
            }}
          />
        )}
        {dynamicMenuItems.map((menuItem: IMenuOption) => {
          const isSelected = currentLevelOneItem?.id === menuItem.id;
          const isLeafNode = itemIsLeafNode(menuItem);
          const menuItemAnchorRef = menuItemAnchorRefs[menuItem.id];

          const handleClick = () => {
            if (menuItem.onClick) menuItem.onClick();

            // Menu is closed or a different item is selected
            if (!isSelected && !isLeafNode) {
              setCurrentLevelOneItem(menuItem);
            }
          };
          return (
            <LevelOneMenuItem
              ref={menuItemAnchorRef}
              item={menuItem}
              handleClick={() => delayedMobileCloseClickHandler(handleClick)}
              isSelected={isSelected}
              showExpandIcon={!isLeafNode}
              key={menuItem.id}
              isActive={menuItem?.isActive}
              firstLevelTwoItemRef={currentFirstLevelTwoItemRef}
              styleOverrides={{ maxWidth: menuItem?.maxWidth }}
            />
          );
        })}
        {showMoreMenuTab && <MoreMenuTab overflowMenuItems={overflowMenuItems} ref={moreMenuTabRef} />}
        {showOfflineIndicator && <OfflineIndicator ref={offlineIndicatorRef} />}
      </Box>
      <SubmenuPopperContainer
        currentLevelOneItem={currentLevelOneItem as IMenuOption}
        isOpen={!!levelTwoLoadingError || (!!popperSubmenuConfig?.anchorEl && !!currentLevelOneItem?.id)}
        anchorEl={popperSubmenuConfig?.anchorEl}
        contentStyleOverrides={{ minWidth: '320px', padding: '8px' }}
        handleClose={() => {
          setCurrentLevelOneItem(null);
        }}
        placement={popperSubmenuConfig?.placement}
        customHeaderErrorMessage={customHeaderErrorMessage}
        disablePortal={true}
      />
    </>
  );
};
